import { useCallback, useEffect, useState } from 'react';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { templateService } from '../services/templateService';
import { OPTIN_PAGE_SIZE } from '../constants/templateApi';

export default function useOptinUsers() {
  const { showSnackBar } = useSnackBar();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isOptingOut, setIsOptingOut] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(OPTIN_PAGE_SIZE);

  const fetchOptinList = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await templateService.getOptinList();
      const list =
        res?.status === 200
          ? Array.isArray(res.data)
            ? res.data
            : res.data
              ? [res.data]
              : []
          : Array.isArray(res)
            ? res
            : res
              ? [res]
              : [];
      setRows(list);
      setTotalCount(list.length);
    } catch (error) {
      showSnackBar(error?.message || 'Failed to fetch optin users', 'error');
      setRows([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [showSnackBar]);

  useEffect(() => {
    fetchOptinList();
  }, [fetchOptinList]);

  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = useCallback((_event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    const limit = parseInt(event.target.value, 10) || OPTIN_PAGE_SIZE;
    setRowsPerPage(limit);
    setPage(0);
  }, []);

  const downloadList = useCallback(async () => {
    setIsDownloading(true);
    try {
      await templateService.downloadOptinList();
    } catch (error) {
      showSnackBar(error?.message || 'Failed to download optin list', 'error');
    } finally {
      setIsDownloading(false);
    }
  }, [showSnackBar]);

  const optoutUser = useCallback(
    async (phoneNumber) => {
      setIsOptingOut(true);
      try {
        const res = await templateService.optoutUser(phoneNumber);
        const msg =
          res?.data?.data?.message ||
          res?.data?.message ||
          res?.message ||
          'User opted out successfully';
        showSnackBar(msg, 'success');
        await fetchOptinList();
        return res;
      } catch (error) {
        const errMsg =
          error?.response?.data?.data?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to opt out user';
        showSnackBar(errMsg, 'error');
        throw error;
      } finally {
        setIsOptingOut(false);
      }
    },
    [fetchOptinList, showSnackBar]
  );

  return {
    rows: paginatedRows,
    totalCount,
    page,
    rowsPerPage,
    isLoading,
    isDownloading,
    isOptingOut,
    handleChangePage,
    handleChangeRowsPerPage,
    refresh: fetchOptinList,
    downloadList,
    optoutUser,
  };
}
